import React, { type JSX } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { InfoListItem, ListItemTag } from '@brightlayer-ui/react-components';
import CheckCircleOutline from '@mui/icons-material/CheckCircleOutline';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import NotificationsActive from '@mui/icons-material/NotificationsActive';
import Warning from '@mui/icons-material/Warning';
import { Divider, Paper, Stack, Typography } from '@mui/material';
import { useTypedSelector } from '@fiji/common/src/app/store';
import { WidgetFooter } from 'pages/WidgetManagement/common';
import { useNewCustomTranslations } from 'hooks';
import { formatEsmNotificationDate } from 'utils/environmentalStressMonitoring';

type Metric = {
    id?: string;
    name: string;
    value: number | string;
    unit?: string;
    severity?: 'alarm' | 'warning' | 'normal';
    status?: 'alarm' | 'warning' | 'normal';
    aggregation?: string;
    displayType?: string;
    icon?: WidgetIcon;
};

type WidgetIcon = {
    web?: { name?: string; style?: { color?: string; backgroundColor?: string } };
    mobile?: { name?: string; style?: { color?: string; backgroundColor?: string } };
};

type Notification = {
    id: string | number;
    title?: string;
    displayName?: string;
    value: number | string;
    unit?: string;
    occurredAt?: string;
    time?: string;
    date?: string;
    type?: 'alarm' | 'warning' | 'offline' | 'normal';
    severity?: 'alarm' | 'warning' | 'offline' | 'normal';
    parameter?: string;
    window?: 'DAILY' | 'MONTHLY';
};

type EnvironmentalStressMonitoringDeviceFilters = { deviceIds?: unknown; deviceId?: unknown };

type EnvironmentalStressMonitoringWidgetData = {
    widgetName?: string;
    overview?: {
        title?: string;
        status?: { label?: string; severity?: 'alarm' | 'warning' | 'normal'; icon?: WidgetIcon };
        metrics?: Metric[];
    };
    secondary?: Notification[];
    statusCount?: { alarms?: number; warnings?: number };
    maxCount?: number;
    total?: number;
    viewAllButton?: boolean;
    viewTitleBar?: boolean;
    showWidgetTitleBar?: boolean;
    detailRoute?: string;
    deviceId?: string;
    organizationId?: string;
    filters?: EnvironmentalStressMonitoringDeviceFilters;
    config?: {
        deviceId?: string;
        organizationId?: string;
        detailRoute?: string;
        filters?: EnvironmentalStressMonitoringDeviceFilters;
    };
    selectedEntities?: { deviceId?: unknown };
};

type EnvironmentalStressMonitoringProps = {
    widgetData?: EnvironmentalStressMonitoringWidgetData;
    mode?: string;
};

type NotificationStatusKey = keyof NonNullable<EnvironmentalStressMonitoringWidgetData['statusCount']>;

const notificationStatuses: ReadonlyArray<{ key: NotificationStatusKey; label: string }> = [
    { key: 'alarms', label: 'Alarm' },
    { key: 'warnings', label: 'Warning' },
];
const notificationTranslationKeys: Record<NotificationStatusKey, 'ALARM' | 'WARNING'> = {
    alarms: 'ALARM',
    warnings: 'WARNING',
};

const formatOccurredAt = (occurredAt?: string): { time: string; date: string } => {
    if (!occurredAt) return { time: '--', date: '' };

    const date = new Date(occurredAt);
    if (Number.isNaN(date.getTime())) return { time: '--', date: '' };

    return {
        time: date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }),
        date: formatEsmNotificationDate(date.getTime()),
    };
};

const getNotificationIcon = (type?: Notification['type']): JSX.Element => {
    if (type === 'alarm') return <NotificationsActive color="error" sx={{ fontSize: 22 }} />;
    if (type === 'warning') return <Warning sx={{ fontSize: 24, color: '#F0AA1F' }} />;
    return <CheckCircleOutline sx={{ fontSize: 18, color: '#9CA5A9' }} />;
};

const getSeverityColors = (severity?: string): { icon: string; badge: string } => {
    if (severity === 'alarm') return { icon: '#D43D3D', badge: '#D43D3D' };
    if (severity === 'warning') return { icon: '#F0AA1F', badge: '#F0AA1F' };
    return { icon: '#9CA5A9', badge: '#9CA5A9' };
};

const getStatusIcon = (icon: WidgetIcon | undefined, severity?: string, fontSize = 16): JSX.Element => {
    const iconName = icon?.web?.name?.toLowerCase() ?? '';
    const colors = getSeverityColors(severity);
    const color = icon?.web?.style?.color ?? colors.icon;
    if (iconName.includes('notification') || iconName.includes('alarm')) {
        return <NotificationsActive sx={{ color, fontSize }} />;
    }
    if (iconName.includes('warning')) return <Warning sx={{ color, fontSize }} />;
    if (iconName.includes('info')) return <InfoOutlined sx={{ color, fontSize }} />;
    if (iconName.includes('check') || (!iconName && severity === 'normal')) {
        return <CheckCircleOutline sx={{ color, fontSize }} />;
    }
    if (!iconName && severity === 'alarm') return <NotificationsActive sx={{ color, fontSize }} />;
    return <Warning sx={{ color, fontSize }} />;
};

const getMetricBadgeColor = (metric: Metric, severity?: string): string => {
    const providedColor = metric.icon?.web?.style?.backgroundColor;
    return providedColor && providedColor.toLowerCase() !== '#ffffff'
        ? providedColor
        : getSeverityColors(severity).badge;
};

const formatNotificationCounts = (
    statusCount: EnvironmentalStressMonitoringWidgetData['statusCount'] | undefined,
    t: TFunction
): string =>
    notificationStatuses
        .filter(({ key }) => (statusCount?.[key] ?? 0) > 0)
        .map(({ key, label }) => {
            const count = statusCount?.[key] ?? 0;
            const translationKey = `WIDGETS:ESM_${notificationTranslationKeys[key]}${count === 1 ? '' : 'S'}`;
            return `${count} ${t(translationKey, { defaultValue: `${label}${count === 1 ? '' : 's'}` })}`;
        })
        .join(', ');

const WidgetHeaderTitle = ({ widgetName, mode }: { widgetName?: string; mode?: string }): JSX.Element => (
    <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={
            mode === 'edit'
                ? { position: 'absolute', top: 0, height: 48, left: 56, right: 56, minWidth: 0 }
                : { height: 48, px: 2, minWidth: 0 }
        }
    >
        <Typography
            variant="subtitle2"
            color="primary"
            fontFamily="Open Sans"
            fontWeight={600}
            fontSize={14}
            lineHeight="20px"
            noWrap
            title={widgetName}
            sx={{ minWidth: 0 }}
        >
            {widgetName}
        </Typography>
    </Stack>
);

const Component = ({ widgetData = {}, mode }: EnvironmentalStressMonitoringProps): JSX.Element => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    useNewCustomTranslations('WIDGETS');
    const configuredDeviceId = useTypedSelector((state) => {
        const { deviceId: storedDeviceId, filters } = state.environmental_stress_monitoring;
        const filterDeviceIds = filters.deviceIds;
        if (Array.isArray(filterDeviceIds) && typeof filterDeviceIds[0] === 'string') return filterDeviceIds[0];
        if (typeof filters.deviceId === 'string') return filters.deviceId;
        return storedDeviceId;
    });
    const selectedEntityDeviceIds = widgetData.selectedEntities?.deviceId;
    const widgetFilters = widgetData.config?.filters ?? widgetData.filters;
    const widgetFilterDeviceIds = widgetFilters?.deviceIds;
    const deviceId =
        (Array.isArray(selectedEntityDeviceIds) && typeof selectedEntityDeviceIds[0] === 'string'
            ? selectedEntityDeviceIds[0]
            : undefined) ??
        (Array.isArray(widgetFilterDeviceIds) && typeof widgetFilterDeviceIds[0] === 'string'
            ? widgetFilterDeviceIds[0]
            : undefined) ??
        (typeof widgetFilters?.deviceId === 'string' ? widgetFilters.deviceId : undefined) ??
        widgetData.config?.deviceId ??
        widgetData.deviceId ??
        configuredDeviceId;
    const overview = widgetData.overview ?? {};
    const metrics = overview.metrics ?? [];
    const notifications = widgetData.secondary ?? [];
    const visibleNotifications = notifications.slice(0, widgetData.maxCount ?? notifications.length);
    const showTitleBar = widgetData.showWidgetTitleBar ?? widgetData.viewTitleBar ?? true;
    return (
        <Paper
            sx={{
                height: '100%',
                overflow: 'hidden',
                containerType: 'inline-size',
                '& .MuiCardActions-root': {
                    maxWidth: '100%',
                    width: '100%',
                    '@supports (width: 1cqw)': {
                        maxWidth: '100cqw',
                        width: '100cqw',
                    },
                    '& > .MuiStack-root': {
                        left: 16,
                        right: 16,
                        width: 'auto',
                        height: '100%',
                        bottom: 0,
                        minWidth: 0,
                    },
                    '& .MuiTypography-root': {
                        fontFamily: 'Open Sans',
                        fontSize: 14,
                        fontWeight: 600,
                        minWidth: 0,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                    },
                    '& .MuiIconButton-root': { flexShrink: 0, minWidth: 44, minHeight: 44 },
                },
            }}
            data-testid="environmental-stress-monitoring-widget"
        >
            {showTitleBar && <WidgetHeaderTitle widgetName={widgetData.widgetName} mode={mode} />}
            {showTitleBar && mode !== 'edit' && <Divider />}
            <Stack sx={{ height: 'calc(100% - 100px)', overflow: 'auto' }}>
                <Stack height={56} px={3} justifyContent="center" sx={{ flexShrink: 0 }} className="border-bottom-1">
                    <Typography
                        color="#424E54"
                        fontFamily="Open Sans"
                        fontSize={16}
                        fontWeight={600}
                        lineHeight="24px"
                        sx={{ fontFeatureSettings: "'liga' off, 'clig' off" }}
                    >
                        {t(overview.title ?? 'WIDGETS:ESM_30_DAY_OVERVIEW', { defaultValue: '30-Day Overview' })}
                    </Typography>
                </Stack>
                <Stack
                    height={147}
                    pl="3.2px"
                    pr={2.5}
                    py="20.5px"
                    direction="row"
                    alignItems="center"
                    gap={2}
                    sx={{ flexShrink: 0, minWidth: 0, '@container (max-width: 360px)': { gap: 1 } }}
                    className="border-bottom-1"
                >
                    <Stack
                        alignItems="center"
                        justifyContent="center"
                        width={125.3}
                        height={106}
                        gap={1}
                        sx={{
                            flexShrink: 0,
                            borderRight: 1,
                            borderColor: 'divider',
                            '@container (max-width: 360px)': {
                                width: 72,
                                '& .MuiSvgIcon-root': { fontSize: 32 },
                                '& .MuiTypography-root': { fontSize: 14 },
                                '& .MuiTypography-root:last-child': { fontSize: 12 },
                            },
                        }}
                    >
                        {getStatusIcon(overview.status?.icon, overview.status?.severity, 36)}
                        <Typography
                            color="#556167"
                            fontFamily="Open Sans"
                            fontSize={18}
                            fontWeight={600}
                            textAlign="center"
                        >
                            {overview.status?.severity === 'normal'
                                ? t('WIDGETS:ESM_EXPECTED')
                                : overview.status?.label ?? t('WIDGETS:ESM_WARNING')}
                        </Typography>
                        <Typography color="#6A767C" fontFamily="Open Sans" fontSize={14} fontWeight={600}>
                            {t('WIDGETS:ESM_STATUS')}
                        </Typography>
                    </Stack>
                    <Stack flex={1} minWidth={0} spacing={0.5} justifyContent="center">
                        {metrics.map((metric) => {
                            const severity = metric.severity ?? metric.status ?? 'normal';
                            return (
                                <Stack
                                    key={metric.id ?? metric.name}
                                    height={18}
                                    direction="row"
                                    alignItems="center"
                                    gap={1.5}
                                    sx={{
                                        minWidth: 0,
                                        '& > .MuiSvgIcon-root': { flexShrink: 0 },
                                        '@container (max-width: 360px)': {
                                            gap: 0.75,
                                            '& > .MuiTypography-root': { fontSize: 12 },
                                        },
                                    }}
                                >
                                    {getStatusIcon(metric.icon, severity, 18)}
                                    <Typography
                                        flex={1}
                                        color="#424E54"
                                        fontFamily="Open Sans"
                                        fontSize={12}
                                        fontWeight={600}
                                        sx={{ minWidth: 0, fontFeatureSettings: "'liga' off, 'clig' off" }}
                                        noWrap
                                        title={metric.name}
                                    >
                                        {metric.name}
                                    </Typography>
                                    <Typography
                                        color="#424E54"
                                        fontFamily="Open Sans"
                                        fontSize={12}
                                        fontWeight={600}
                                        sx={{ flexShrink: 0, fontFeatureSettings: "'liga' off, 'clig' off" }}
                                        whiteSpace="nowrap"
                                    >
                                        {metric.value} <span style={{ fontWeight: 300 }}>{metric.unit}</span>
                                    </Typography>
                                    <ListItemTag
                                        label={
                                            metric.aggregation ??
                                            metric.displayType ??
                                            t('WIDGETS:ESM_AVERAGE_ABBREVIATION', { defaultValue: 'AVG' })
                                        }
                                        backgroundColor={getMetricBadgeColor(metric, severity)}
                                        fontColor="#FFFFFF"
                                        sx={{
                                            flexShrink: 0,
                                            minWidth: 31,
                                            height: 16,
                                            borderRadius: '2px',
                                            '& .MuiTypography-root': {
                                                fontFamily: 'Open Sans',
                                                fontSize: '10px',
                                                fontWeight: 700,
                                                lineHeight: '16px',
                                                letterSpacing: 0,
                                                textTransform: 'uppercase',
                                                fontFeatureSettings: "'liga' off, 'clig' off",
                                            },
                                        }}
                                    />
                                </Stack>
                            );
                        })}
                    </Stack>
                </Stack>
                <Stack
                    minHeight={48}
                    px={2}
                    py={1}
                    justifyContent="center"
                    sx={{ flexShrink: 0 }}
                    className="border-bottom-1"
                >
                    <Typography color="#424E54" fontFamily="Open Sans" fontSize={16} fontWeight={600} lineHeight="24px">
                        {t('WIDGETS:ESM_NOTIFICATIONS', { defaultValue: 'Notifications' })}{' '}
                        <span style={{ fontWeight: 400 }}>{formatNotificationCounts(widgetData.statusCount, t)}</span>
                    </Typography>
                </Stack>
                {visibleNotifications.map((notification) => {
                    const occurredAt = formatOccurredAt(notification.occurredAt);
                    const time = notification.time ?? occurredAt.time;
                    const date = occurredAt.date || notification.date || '';
                    return (
                        <InfoListItem
                            key={notification.id}
                            data-testid={`environmental-stress-monitoring-notification-${notification.id}`}
                            divider="full"
                            onClick={
                                mode === 'edit'
                                    ? undefined
                                    : (): void =>
                                          navigate(
                                              `${(
                                                  widgetData.detailRoute ??
                                                  widgetData.config?.detailRoute ??
                                                  '/environmental-stress-monitoring/details'
                                              ).replace(/\/$/, '')}/${encodeURIComponent(String(notification.id))}`
                                          )
                            }
                            ripple={mode !== 'edit'}
                            sx={{
                                height: 62,
                                minHeight: 62,
                                flexShrink: 0,
                                pl: mode === 'edit' ? 3 : 0,
                                pr: mode === 'edit' ? 2 : 0,
                                py: 0,
                                '& .MuiListItemButton-root': { height: 62, pl: 3, pr: 2, py: 0, minWidth: 0 },
                                '& .MuiListItemAvatar-root': { flexShrink: 0 },
                                '& .BluiInfoListItem-icon': { width: 24, mr: 3 },
                                '& .BluiInfoListItem-listItemText': { minWidth: 0 },
                                '@container (max-width: 360px)': {
                                    px: mode === 'edit' ? 1.5 : 0,
                                    '& .MuiListItemButton-root': { px: 1.5 },
                                    '& .BluiInfoListItem-icon': { width: 20, mr: 1 },
                                    '& .BluiInfoListItem-listItemText': { ml: 1 },
                                    '& .BluiInfoListItem-rightComponent': { ml: 1 },
                                },
                            }}
                            icon={getNotificationIcon(notification.severity ?? notification.type)}
                            title={
                                <Typography
                                    color="#424E54"
                                    fontFamily="Open Sans"
                                    fontSize={16}
                                    fontWeight={600}
                                    sx={{ '@container (max-width: 360px)': { fontSize: 14 } }}
                                    noWrap
                                    title={notification.displayName ?? notification.title ?? ''}
                                >
                                    {notification.displayName ?? notification.title ?? ''}
                                </Typography>
                            }
                            leftComponent={
                                <Stack
                                    width={64}
                                    sx={{ flexShrink: 0, '@container (max-width: 360px)': { width: 56 } }}
                                >
                                    <Typography
                                        color="#424E54"
                                        fontFamily="Open Sans"
                                        fontSize={12}
                                        fontWeight={700}
                                        lineHeight="16px"
                                        whiteSpace="nowrap"
                                    >
                                        {time}
                                    </Typography>
                                    <Typography
                                        color="#424E54"
                                        fontFamily="Open Sans"
                                        fontSize={12}
                                        lineHeight="16px"
                                        whiteSpace="nowrap"
                                    >
                                        {date}
                                    </Typography>
                                </Stack>
                            }
                            rightComponent={
                                <Typography
                                    color="#424E54"
                                    fontFamily="Open Sans"
                                    fontSize={14}
                                    fontWeight={600}
                                    whiteSpace="nowrap"
                                    sx={{ '@container (max-width: 360px)': { fontSize: 14 } }}
                                >
                                    {notification.value} <span style={{ fontWeight: 300 }}>{notification.unit}</span>
                                </Typography>
                            }
                        />
                    );
                })}
            </Stack>
            <WidgetFooter
                widgetData={{
                    ...widgetData,
                    deviceId,
                    organizationId: widgetData.organizationId ?? widgetData.config?.organizationId,
                }}
                ids={[]}
                mode={mode}
                actionBtnName={t('WIDGETS:ESM_VIEW_ALL', {
                    defaultValue: 'View All Environmental Stress Monitoring',
                })}
            />
        </Paper>
    );
};

export default Component;
